import { FRAXLEND_PAIR_ABI } from '@/config/abis/FraxlendPair.abi'
import { FRAXLEND_PAIR_REGISTRY_ABI } from '@/config/abis/FraxlendPairRegistry.abi'
import { FRAX_ORACLE_ABI } from '@/config/abis/fraxOracleAbi.abi'
import { LISTING_REFETCH_INTERVAL, LISTING_STALE_TIME } from '@/config/consts'
import { useChainConfigEntry } from '@/domain/hooks/useChainConfigEntry'
import { getDisplayToken } from '@/domain/hooks/useDisplayToken'
import { usePageChainId } from '@/domain/hooks/usePageChainId'
import { useMarketInfoQuery } from '@/domain/market-info/useMarketInfo'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { NormalizedUnitNumber, Percentage } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import type { FraxlendPairPauseState } from '@/features/isolated/types/FraxlendPairPauseState'
import { MarketStats } from '@/features/markets/logic/aggregate-stats'
import { useMemo } from 'react'
import { Address, formatUnits, zeroAddress } from 'viem'
import { useAccount, useReadContract, useReadContracts } from 'wagmi'

const SECONDS_PER_YEAR = 31_556_952

const ERC20_ABI = [
  { inputs: [], name: 'decimals', outputs: [{ type: 'uint8' }], stateMutability: 'view', type: 'function' },
  { inputs: [], name: 'symbol', outputs: [{ type: 'string' }], stateMutability: 'view', type: 'function' },
  { inputs: [], name: 'name', outputs: [{ type: 'string' }], stateMutability: 'view', type: 'function' },
  {
    inputs: [{ type: 'address' }],
    name: 'balanceOf',
    outputs: [{ type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
] as const

/** Options for reads whose result never changes within a session (token metadata, registry, constants). */
const STRUCTURAL_QUERY_OPTIONS = {
  staleTime: Number.POSITIVE_INFINITY,
  gcTime: Number.POSITIVE_INFINITY,
  refetchInterval: false,
  refetchOnWindowFocus: false,
  refetchOnMount: false,
} as const

/** Options for reads that reflect live on-chain state (accounting, rates, prices, balances). */
const LIVE_QUERY_OPTIONS = {
  staleTime: LISTING_STALE_TIME,
  refetchInterval: LISTING_REFETCH_INTERVAL,
  refetchOnWindowFocus: false,
} as const

// Slot layout inside each flattened multicall payload. Keeping these as
// constants makes the index math below self-documenting.
const PAIR_STATIC_SLOTS = 5 // decimals, collateralContract, asset, maxLTV, getConstants
const PAIR_LIVE_SLOTS = 3 // getPairAccounting, previewAddInterest, exchangeRateInfo
const TOKEN_INFO_SLOTS = 6 // collat: decimals/symbol/name + borrow: decimals/symbol/name
const USER_SLOTS = 2 // getUserSnapshot, borrow token balanceOf
/** Live FraxlendPair pause flags (see `isInterestPaused`, etc.). */
const PAIR_PAUSE_SLOTS = 4

const calculateCollateralValueUSD = (pair: PairDetails): number => {
  return (
    (Number(pair.collateralToken.unitPriceUsd) * Number(pair.totalCollateral)) / 10 ** pair.collateralToken.decimals
  )
}

export type PairDetails = {
  address: Address
  collateralToken: Token
  borrowToken: Token
  totalCollateral: bigint
  totalBorrow: bigint
  totalAssetAmount: bigint
  totalAssetShares: bigint
  available: bigint
  utilization: number
  borrowAPY: number
  lendAPY: number
  decimals: number
  maxLtv: Percentage
  fraxlendPauseState: FraxlendPairPauseState
}

export interface IsolatedUserData {
  userAssetShares: bigint
  userCollateralBalance: bigint
  walletBalance: NormalizedUnitNumber
}

export function useIsolatedPairs(): {
  pairs: PairDetails[]
  marketStats: MarketStats
  isLoading: boolean
  userDataMap: Map<string, IsolatedUserData>
} {
  const { isolated } = useChainConfigEntry()
  const { chainId } = usePageChainId()
  const { address: userAddress } = useAccount()

  const { marketInfo, isPending: isLoadingMarketInfo } = useMarketInfoQuery({
    chainId,
    refetchInterval: LISTING_REFETCH_INTERVAL,
  })

  // Wave 0: registry lookup. Pair set is effectively static per session.
  const { data: pairAddresses, isLoading: isLoadingAddresses } = useReadContract({
    address: isolated?.registryAddress,
    abi: FRAXLEND_PAIR_REGISTRY_ABI,
    functionName: 'getAllPairAddresses',
    chainId,
    query: STRUCTURAL_QUERY_OPTIONS,
  })

  // Wave 1a (structural, one multicall): pair-level constants that don't
  // meaningfully change within a session. Collapsed from 5 separate
  // useReadContracts into a single batched read with Infinity staleTime.
  const pairStaticReads = useMemo(() => {
    if (!pairAddresses || pairAddresses.length === 0) return []
    return pairAddresses.flatMap((address) => {
      const pair = address as Address
      return [
        { address: pair, abi: FRAXLEND_PAIR_ABI, functionName: 'decimals' as const, chainId },
        { address: pair, abi: FRAXLEND_PAIR_ABI, functionName: 'collateralContract' as const, chainId },
        { address: pair, abi: FRAXLEND_PAIR_ABI, functionName: 'asset' as const, chainId },
        { address: pair, abi: FRAXLEND_PAIR_ABI, functionName: 'maxLTV' as const, chainId },
        { address: pair, abi: FRAXLEND_PAIR_ABI, functionName: 'getConstants' as const, chainId },
      ]
    })
  }, [pairAddresses, chainId])

  const { data: pairStaticData, isLoading: isLoadingPairStatic } = useReadContracts({
    contracts: pairStaticReads,
    query: {
      ...STRUCTURAL_QUERY_OPTIONS,
      enabled: pairStaticReads.length > 0,
    },
  }) as { data: any[] | undefined; isLoading: boolean }

  // Wave 1b (live, one multicall): pair accounting + rates + exchange rate
  // info. Refreshes at listing cadence instead of each read polling at the
  // 20s global default.
  const pairLiveReads = useMemo(() => {
    if (!pairAddresses || pairAddresses.length === 0) return []
    return pairAddresses.flatMap((address) => {
      const pair = address as Address
      return [
        { address: pair, abi: FRAXLEND_PAIR_ABI, functionName: 'getPairAccounting' as const, chainId },
        { address: pair, abi: FRAXLEND_PAIR_ABI, functionName: 'previewAddInterest' as const, chainId },
        { address: pair, abi: FRAXLEND_PAIR_ABI, functionName: 'exchangeRateInfo' as const, chainId },
      ]
    })
  }, [pairAddresses, chainId])

  const { data: pairLiveData, isLoading: isLoadingPairLive } = useReadContracts({
    contracts: pairLiveReads,
    query: {
      ...LIVE_QUERY_OPTIONS,
      enabled: pairLiveReads.length > 0,
    },
  }) as { data: any[] | undefined; isLoading: boolean }

  const pairPauseReads = useMemo(() => {
    if (!pairAddresses || pairAddresses.length === 0) return []
    return pairAddresses.flatMap((address) => {
      const pair = address as Address
      return [
        { address: pair, abi: FRAXLEND_PAIR_ABI, functionName: 'isInterestPaused' as const, chainId },
        { address: pair, abi: FRAXLEND_PAIR_ABI, functionName: 'isLiquidatePaused' as const, chainId },
        { address: pair, abi: FRAXLEND_PAIR_ABI, functionName: 'isRepayPaused' as const, chainId },
        { address: pair, abi: FRAXLEND_PAIR_ABI, functionName: 'isWithdrawPaused' as const, chainId },
      ]
    })
  }, [pairAddresses, chainId])

  const { data: pairPauseData, isLoading: isLoadingPairPause } = useReadContracts({
    contracts: pairPauseReads,
    query: {
      ...LIVE_QUERY_OPTIONS,
      enabled: pairPauseReads.length > 0,
    },
  }) as { data: any[] | undefined; isLoading: boolean }

  // Wave 2a (structural, depends on 1a): collateral + borrow token metadata.
  // Still never refetches — once we know the token addresses, decimals/symbol/name
  // are immutable.
  const tokenInfoReads = useMemo(() => {
    if (!pairAddresses || pairAddresses.length === 0 || !pairStaticData) return []
    return pairAddresses.flatMap((_, index) => {
      const base = index * PAIR_STATIC_SLOTS
      const collateralAddress = (pairStaticData[base + 1]?.result as Address | undefined) ?? zeroAddress
      const borrowAddress = (pairStaticData[base + 2]?.result as Address | undefined) ?? zeroAddress
      return [
        { address: collateralAddress, abi: ERC20_ABI, functionName: 'decimals' as const, chainId },
        { address: collateralAddress, abi: ERC20_ABI, functionName: 'symbol' as const, chainId },
        { address: collateralAddress, abi: ERC20_ABI, functionName: 'name' as const, chainId },
        { address: borrowAddress, abi: ERC20_ABI, functionName: 'decimals' as const, chainId },
        { address: borrowAddress, abi: ERC20_ABI, functionName: 'symbol' as const, chainId },
        { address: borrowAddress, abi: ERC20_ABI, functionName: 'name' as const, chainId },
      ]
    })
  }, [pairAddresses, pairStaticData, chainId])

  const { data: tokenInfos, isLoading: isLoadingTokenInfos } = useReadContracts({
    contracts: tokenInfoReads,
    query: {
      ...STRUCTURAL_QUERY_OPTIONS,
      enabled: tokenInfoReads.length > 0,
    },
  }) as { data: any[] | undefined; isLoading: boolean }

  // Wave 2b (live, depends on 1b): direct oracle prices. Oracle addresses
  // come from the exchangeRateInfo slot in pairLiveData.
  const oracleReads = useMemo(() => {
    if (!pairAddresses || pairAddresses.length === 0 || !pairLiveData) return []
    return pairAddresses.map((_, index) => {
      const base = index * PAIR_LIVE_SLOTS
      const info = pairLiveData[base + 2]?.result as [Address, bigint, bigint, bigint, bigint] | undefined
      return {
        address: info?.[0] ?? zeroAddress,
        abi: FRAX_ORACLE_ABI,
        functionName: 'getPrices' as const,
        chainId,
      }
    })
  }, [pairAddresses, pairLiveData, chainId])

  const { data: directOraclePricesInfos, isLoading: isLoadingDirectOraclePricesInfos } = useReadContracts({
    contracts: oracleReads,
    query: {
      ...LIVE_QUERY_OPTIONS,
      enabled: oracleReads.length > 0,
    },
  })

  // Wave 2c (live, user-gated): per-pair userSnapshot + borrow-token
  // balanceOf, collapsed into a single multicall that only runs when a wallet
  // is connected.
  const userReads = useMemo(() => {
    if (!userAddress || !pairAddresses || pairAddresses.length === 0 || !pairStaticData) return []
    return pairAddresses.flatMap((pair, index) => {
      const base = index * PAIR_STATIC_SLOTS
      const borrowAddress = (pairStaticData[base + 2]?.result as Address | undefined) ?? zeroAddress
      return [
        {
          address: pair as Address,
          abi: FRAXLEND_PAIR_ABI,
          functionName: 'getUserSnapshot' as const,
          args: [userAddress],
          chainId,
        },
        {
          address: borrowAddress,
          abi: ERC20_ABI,
          functionName: 'balanceOf' as const,
          args: [userAddress],
          chainId,
        },
      ]
    })
  }, [userAddress, pairAddresses, pairStaticData, chainId])

  const { data: userData } = useReadContracts({
    contracts: userReads,
    query: {
      ...LIVE_QUERY_OPTIONS,
      enabled: userReads.length > 0,
    },
  }) as { data: any[] | undefined; isLoading: boolean }

  const pairs: PairDetails[] = pairAddresses
    ? pairAddresses.map((address: Address, index: number) => {
        const staticBase = index * PAIR_STATIC_SLOTS
        const liveBase = index * PAIR_LIVE_SLOTS
        const tokenBase = index * TOKEN_INFO_SLOTS

        const decimalsResult = pairStaticData?.[staticBase + 0]?.result as number | undefined
        const collateralContractResult = pairStaticData?.[staticBase + 1]?.result as Address | undefined
        const assetContractResult = pairStaticData?.[staticBase + 2]?.result as Address | undefined
        const maxLtvRaw = pairStaticData?.[staticBase + 3]?.result as bigint | undefined
        const constants = pairStaticData?.[staticBase + 4]?.result as
          | readonly [bigint, bigint, bigint, bigint, bigint, bigint, bigint, bigint]
          | undefined

        const accountingResult = pairLiveData?.[liveBase + 0]?.result as
          | [bigint, bigint, bigint, bigint, bigint]
          | undefined
        const rateInfoResult = pairLiveData?.[liveBase + 1]?.result as
          | [
              bigint,
              bigint,
              bigint,
              { lastBlock: number; feeToProtocolRate: number; lastTimestamp: bigint; ratePerSec: bigint },
              { amount: bigint; shares: bigint },
              { amount: bigint; shares: bigint },
            ]
          | undefined
        const exchangeRateInfo = pairLiveData?.[liveBase + 2]?.result as
          | readonly [Address, bigint, bigint, bigint, bigint]
          | undefined

        const totalAssetAmount = accountingResult?.[0] || 0n
        const totalAssetShares = accountingResult?.[1] || 0n
        const totalBorrow = accountingResult?.[2] || 0n
        const totalBorrowShares = accountingResult?.[3] || 0n
        const totalCollateral = accountingResult?.[4] || 0n

        const available = totalAssetAmount - totalBorrow
        const utilization =
          totalAssetAmount > 0n ? Number((totalBorrowShares * 1000000n) / totalAssetShares) / 10000 : 0

        const ratePerSec = rateInfoResult?.[3].ratePerSec ? Number(rateInfoResult[3].ratePerSec) / 1e18 : 0
        // feeToProtocolRate is 1e5 precision
        // see: https://github.com/FraxFinance/fraxlend-audit/blob/76c537d429ea4ac9bed2b13fc2cca90cccb2791a/src/contracts/FraxlendPairCore.sol#L103
        const feeToProtocolRate = rateInfoResult?.[3].feeToProtocolRate
          ? Number(rateInfoResult[3].feeToProtocolRate) / 1e5
          : 0

        const borrowAPY = ratePerSec > 0 ? (Math.exp(ratePerSec * SECONDS_PER_YEAR) - 1) * 100 : 0
        const lendAPY = borrowAPY * (utilization / 100) * (1 - feeToProtocolRate)

        const decimals = decimalsResult || 18

        const collateralDecimals = tokenInfos?.[tokenBase + 0]?.result as number
        const collateralSymbol = tokenInfos?.[tokenBase + 1]?.result as string
        const collateralName = tokenInfos?.[tokenBase + 2]?.result as string
        const borrowDecimals = tokenInfos?.[tokenBase + 3]?.result as number
        const borrowSymbol = tokenInfos?.[tokenBase + 4]?.result as string
        const borrowName = tokenInfos?.[tokenBase + 5]?.result as string

        const directOraclePrices = directOraclePricesInfos?.[index]?.result as [boolean, bigint, bigint] | undefined

        const [_oracleAddress, _maxOracleDeviation, _oracleLastTimestamp, lowExchangeRate, _highExchangeRate] =
          exchangeRateInfo || []

        const collateralReserveToken = marketInfo?.reserves.find((r) => r.token.address === collateralContractResult)
        const borrowReserveToken = marketInfo?.reserves.find((r) => r.token.address === assetContractResult)

        let collateralPrice = collateralReserveToken?.priceInUSD.toString() || '0'
        let borrowPrice = borrowReserveToken?.priceInUSD.toString() || '0'

        const [
          _LTV_PRECISION,
          _LIQ_PRECISION,
          _UTIL_PREC,
          _FEE_PRECISION,
          _EXCHANGE_PRECISION,
          _DEVIATION_PRECISION,
          _RATE_PRECISION,
          _MAX_PROTOCOL_FEE,
        ] = constants || []

        const currentExchangeRate =
          (Number(directOraclePrices ? Number(directOraclePrices[1]) : Number(lowExchangeRate)) /
            Number(_EXCHANGE_PRECISION)) *
          10 ** (borrowDecimals - collateralDecimals)

        if (collateralPrice === '0' && borrowPrice !== '0') {
          const calculatedPrice = Number(borrowPrice) * Number(1 / currentExchangeRate)
          collateralPrice = !Number.isNaN(calculatedPrice) ? calculatedPrice.toString() : '0'
        } else if (borrowPrice === '0' && collateralPrice !== '0') {
          const calculatedPrice = Number(collateralPrice) * currentExchangeRate
          borrowPrice = !Number.isNaN(calculatedPrice) ? calculatedPrice.toString() : '0'
        } else if (collateralPrice === '0' && borrowPrice === '0') {
          collateralPrice = '0'
          borrowPrice = '0'
        }

        if (Number.isNaN(Number(collateralPrice))) collateralPrice = '0'
        if (Number.isNaN(Number(borrowPrice))) borrowPrice = '0'

        const maxLtv = maxLtvRaw ? Percentage(Number(maxLtvRaw) / 100000, true, true) : Percentage(0, true, true)

        const pauseBase = index * PAIR_PAUSE_SLOTS
        const fraxlendPauseState: FraxlendPairPauseState = {
          interestPaused: Boolean(pairPauseData?.[pauseBase + 0]?.result),
          liquidatePaused: Boolean(pairPauseData?.[pauseBase + 1]?.result),
          repayPaused: Boolean(pairPauseData?.[pauseBase + 2]?.result),
          withdrawPaused: Boolean(pairPauseData?.[pauseBase + 3]?.result),
        }

        return {
          address,
          collateralToken: new Token({
            address: CheckedAddress(collateralContractResult || '0x0000000000000000000000000000000000000000'),
            symbol: TokenSymbol(collateralSymbol || 'e'),
            name: collateralName || collateralSymbol || '',
            decimals: collateralDecimals || 18,
            unitPriceUsd: collateralPrice,
          }),
          borrowToken: new Token({
            address: CheckedAddress(assetContractResult || '0x0000000000000000000000000000000000000000'),
            symbol: TokenSymbol(borrowSymbol || 'e'),
            name: borrowName || borrowSymbol || '',
            decimals: borrowDecimals || 18,
            unitPriceUsd: borrowPrice,
          }),
          totalCollateral,
          totalBorrow,
          totalAssetAmount,
          totalAssetShares,
          available,
          utilization,
          borrowAPY,
          lendAPY,
          decimals,
          maxLtv,
          fraxlendPauseState,
        }
      })
    : []

  const userDataMap = new Map<string, IsolatedUserData>()
  if (pairAddresses && userData) {
    pairAddresses.forEach((pairAddress, index) => {
      const userBase = index * USER_SLOTS
      const snapshotResult = userData[userBase + 0]?.result as [bigint, bigint, bigint] | undefined
      const walletResult = userData[userBase + 1]?.result as bigint | undefined
      const userAssetShares = snapshotResult?.[0] ?? 0n
      const userCollateralBalance = snapshotResult?.[2] ?? 0n
      const tokenBase = index * TOKEN_INFO_SLOTS
      const borrowDecimals = (tokenInfos?.[tokenBase + 3]?.result as number) || 18
      const walletBalanceRaw = walletResult ?? 0n
      const walletBalance = NormalizedUnitNumber(formatUnits(walletBalanceRaw, borrowDecimals))

      userDataMap.set(pairAddress.toLowerCase(), {
        userAssetShares,
        userCollateralBalance,
        walletBalance,
      })
    })
  }

  const marketStats = pairs?.reduce(
    (acc, pair) => {
      const lendAvailableValueUSD =
        (Number(pair.borrowToken.unitPriceUsd) * Number(pair.available)) / 10 ** pair.borrowToken.decimals
      const collateralValueUSD = calculateCollateralValueUSD(pair)
      const borrowValueUSD =
        (Number(pair.borrowToken.unitPriceUsd) * Number(pair.totalBorrow)) / 10 ** pair.borrowToken.decimals
      return {
        totalMarketSizeUSD: NormalizedUnitNumber(
          acc.totalMarketSizeUSD.plus(
            NormalizedUnitNumber(lendAvailableValueUSD + borrowValueUSD + collateralValueUSD),
          ),
        ),
        totalValueLockedUSD: NormalizedUnitNumber(
          acc.totalValueLockedUSD.plus(NormalizedUnitNumber(collateralValueUSD)),
        ),
        totalAvailableUSD: NormalizedUnitNumber(
          acc.totalAvailableUSD.plus(NormalizedUnitNumber(lendAvailableValueUSD)),
        ),
        totalBorrowsUSD: NormalizedUnitNumber(acc.totalBorrowsUSD.plus(NormalizedUnitNumber(borrowValueUSD))),
      }
    },
    {
      totalMarketSizeUSD: NormalizedUnitNumber(0),
      totalValueLockedUSD: NormalizedUnitNumber(0),
      totalAvailableUSD: NormalizedUnitNumber(0),
      totalBorrowsUSD: NormalizedUnitNumber(0),
    },
  )

  return {
    pairs: pairs
      .map((pair) => ({
        ...pair,
        collateralToken: getDisplayToken(chainId, pair.collateralToken),
        borrowToken: getDisplayToken(chainId, pair.borrowToken),
      }))
      .sort((a, b) => calculateCollateralValueUSD(b) - calculateCollateralValueUSD(a)),
    marketStats,
    userDataMap,
    isLoading:
      isLoadingAddresses ||
      isLoadingMarketInfo ||
      isLoadingPairStatic ||
      isLoadingPairLive ||
      isLoadingPairPause ||
      isLoadingTokenInfos ||
      isLoadingDirectOraclePricesInfos,
  }
}
